import React, { useMemo, useRef, useState } from "react";
// eslint-disable-next-line
import { Card, Segmented, Select, Skeleton } from "antd";
import { TREND_METRICS } from "../constants/npsConstants";
import NPSCardHead from "./NPSCardHead";
import NPSChevronIcon from "./NPSChevronIcon";
import "./NPSTrendChart.css";

const W = 1200;
const H = 280;
const L = 52;
const R = 24;
const T = 26;
const B = 42;
 // eslint-disable-next-line
const RANGE_OPTIONS = [
  { label: "3M", value: 3 },
  { label: "6M", value: 6 },
  { label: "12M", value: 12 },
];

const NPSTrendChart = ({ history, isLoading }) => {
  const svgRef = useRef(null);
   // eslint-disable-next-line
  const [range, setRange] = useState(6);
  const [metric, setMetric] = useState("nps");
  const [hoverIndex, setHoverIndex] = useState(null);

  const data = useMemo(() => history.slice(-range), [history, range]);
  const M = TREND_METRICS[metric];

  const layout = useMemo(() => {
    const vals = data.map((d) => d[metric]);
    const step = metric === "avg" ? 0.5 : 5;
    const lo = Math.floor((Math.min(...vals) - step) / step) * step;
    const hi = Math.ceil((Math.max(...vals) + step) / step) * step;
    const x = (i) => L + (i * (W - L - R)) / Math.max(data.length - 1, 1);
    const y = (v) => T + ((hi - v) / (hi - lo)) * (H - T - B);
    const points = data.map((d, i) => [x(i), y(d[metric])]);
    const gridLines = [];
    for (let v = lo; v <= hi + 1e-9; v += step) gridLines.push(v);
    return { points, gridLines, y };
  }, [data, metric]);

  const stroke = metric === "detractors" ? "#E0483D" : "#2A5EDF";
  const fill = metric === "detractors" ? "#FCECEA" : "#EAF0FE";

  const areaPath = layout.points.length
    ? `M ${layout.points[0][0]} ${H - B} L ${layout.points.map((p) => p.join(" ")).join(" L ")} L ${layout.points.at(-1)[0]} ${H - B} Z`
    : "";
  const linePath = layout.points.map((p) => p.join(",")).join(" ");

  const peak = data.length ? data.reduce((a, b) => (b[metric] > a[metric] ? b : a)) : null;
  const low = data.length ? data.reduce((a, b) => (b[metric] < a[metric] ? b : a)) : null;
  const change = data.length ? data.at(-1)[metric] - data[0][metric] : 0;
  const changeGood = metric === "detractors" ? change <= 0 : change >= 0;

  const handleMove = (e) => {
    if (!layout.points.length || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    const mx = (e.clientX - box.left) * (W / box.width);
    let idx = 0;
    let best = Infinity;
    layout.points.forEach(([px], i) => {
      const d = Math.abs(px - mx);
      if (d < best) { best = d; idx = i; }
    });
    setHoverIndex(idx);
  };

  const hovered = hoverIndex != null ? { point: layout.points[hoverIndex], d: data[hoverIndex] } : null;

  if (isLoading) {
    return (
      <Card className="nps-card nps-trend-card" bodyStyle={{ padding: 0 }}>
        <NPSCardHead title="NPS trend" note="Loading trend…" />
        <div className="nps-trend-body">
          <Skeleton.Node active style={{ width: "100%", height: 240 }}>
            <span />
          </Skeleton.Node>
        </div>
        <div className="nps-trend-legend">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton.Input active size="small" key={i} style={{ width: 130 }} />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="nps-card nps-trend-card" bodyStyle={{ padding: 0 }}>
      <NPSCardHead
        title="NPS trend"
        // note={`${M.label} · last ${range} months`}
         note={`${M.label} · current month`}
        right={
          <div className="nps-trend-controls">
            {/* <Segmented
              options={RANGE_OPTIONS}
              value={range}
              onChange={(v) => { setRange(v); setHoverIndex(null); }}
            /> */}
            <Select
              value={metric}
              style={{ width: 160 }}
              onChange={(v) => { setMetric(v); setHoverIndex(null); }}
              options={Object.entries(TREND_METRICS).map(([value, m]) => ({ value, label: m.label }))}
              suffixIcon={<NPSChevronIcon />}
            />
          </div>
        }
      />
      <div className="nps-trend-body">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`${M.label} trend, last ${range} months`}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {layout.gridLines.map((v) => (
            <React.Fragment key={v}>
              <line x1={L} x2={W - R} y1={layout.y(v)} y2={layout.y(v)} stroke="#EEF1F5" strokeWidth={1} />
              <text x={L - 12} y={layout.y(v) + 4} fontSize={12} fill="#8496A8" textAnchor="end">{M.format(v)}</text>
            </React.Fragment>
          ))}
          {areaPath && <path d={areaPath} fill={fill} opacity={0.7} />}
          {linePath && (
            <polyline points={linePath} fill="none" stroke={stroke} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {hovered && (
            <line x1={hovered.point[0]} x2={hovered.point[0]} y1={T - 6} y2={H - B} stroke="#C9D3DE" strokeWidth={1} strokeDasharray="4 4" />
          )}
          {hovered && <circle cx={hovered.point[0]} cy={hovered.point[1]} r={9} fill={stroke} opacity={0.22} />}
          {layout.points.map(([px, py], i) => (
            <circle key={i} cx={px} cy={py} r={4} fill="#fff" stroke={stroke} strokeWidth={2} />
          ))}
          {data.map((d, i) => (
            <text key={d.month} x={layout.points[i][0]} y={H - 14} fontSize={12} fill="#8496A8" textAnchor="middle">{d.month}</text>
          ))}
          <rect className="nps-trend-hit" x={0} y={0} width={W} height={H} fill="transparent" />
        </svg>
        {hovered && (
          <div
            className="nps-trend-tip"
            style={{ left: `${(hovered.point[0] / W) * 100}%`, top: `${(hovered.point[1] / H) * 100}%` }}
          >
            <div className="nps-trend-tip-month">{hovered.d.month}</div>
            <div className="nps-trend-tip-value">{M.format(hovered.d[metric])}</div>
            <div className="nps-trend-tip-count">{hovered.d.count} responses</div>
          </div>
        )}
      </div>
      <div className="nps-trend-legend">
        <span>Peak <b>{peak ? `${M.format(peak[metric])} · ${peak.month}` : "–"}</b></span>
        <span>Low <b>{low ? `${M.format(low[metric])} · ${low.month}` : "–"}</b></span>
        <span>
          Change over period{" "}
          <b style={{ color: change === 0 ? undefined : changeGood ? "#1E874B" : "#B3372C" }}>
            {change > 0 ? "+" : ""}{change.toFixed(2)}{M.suffix}
          </b>
        </span>
      </div>
    </Card>
  );
};

export default NPSTrendChart;
